import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Box, Typography, Grid, Paper, Collapse, IconButton, CircularProgress, LinearProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { MaturityBadge } from '../../Shared';
import { getFrameworkRequirements, getPreviewFrameworkRequirements } from 'services/transparency-service';

/**
 * FrameworksTab - Detailed framework view with expandable requirements
 * @param {Array} frameworks - Framework list
 * @param {string} companyKey - Company slug
 * @param {string} accessToken - Optional access token
 */
export const FrameworksTab = ({ frameworks = [], companyKey, accessToken, isPreview = false }) => {
  const { t } = useTranslation('transparency');
  const [expandedFramework, setExpandedFramework] = useState(null);

  const toggleFramework = (frameworkId) => {
    setExpandedFramework(expandedFramework === frameworkId ? null : frameworkId);
  };

  if (!frameworks || frameworks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('protected.emptyFrameworks')}
      </Typography>
    );
  }

  return (
    <Box>
      {frameworks.map((framework, index) => (
        <FrameworkCard
          key={framework.frameworkId || index}
          framework={framework}
          companyKey={companyKey}
          accessToken={accessToken}
          isPreview={isPreview}
          isExpanded={expandedFramework === framework.frameworkId}
          onToggle={() => toggleFramework(framework.frameworkId)}
        />
      ))}
    </Box>
  );
};

const FrameworkCard = ({ framework, companyKey, accessToken, isPreview, isExpanded, onToggle }) => {
  const { t } = useTranslation('transparency');
  const { data: requirementsData, isLoading } = useQuery(
    ['frameworkRequirements', framework.frameworkId, companyKey, isPreview],
    () => isPreview
      ? getPreviewFrameworkRequirements(framework.frameworkId, companyKey)
      : getFrameworkRequirements(framework.frameworkId, companyKey, accessToken),
    { enabled: isExpanded && !!framework.frameworkId }
  );

  const requirements = requirementsData?.data?.requirements || [];
  const completionRate = framework.completionRate || 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Framework Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { bgcolor: '#f5f5f5' },
        }}
        onClick={onToggle}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {framework.name}
            </Typography>
            <MaturityBadge label={framework.maturityLabel} size="small" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1, maxWidth: 200 }}>
              <LinearProgress
                variant="determinate"
                value={completionRate}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor: completionRate >= 80 ? '#4caf50' : completionRate >= 50 ? '#ff9800' : '#f44336',
                  },
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t('protected.frameworks.completion', { rate: completionRate })}
            </Typography>
          </Box>
        </Box>
        <IconButton>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expanded Requirements */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2, pt: 0, borderTop: '1px solid #e0e0e0' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : requirements.length > 0 ? (
            <Grid container spacing={1}>
              {requirements.map((req, index) => (
                <Grid item xs={12} key={req.id || index}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: '#f8fafc',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2">{req.name}</Typography>
                    <MaturityBadge label={req.maturityLabel} size="small" />
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {t('protected.emptyRequirements')}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};
